import { useState, useEffect, useRef, Fragment } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ExternalLink, Pencil, Trash2, Plus, Search, Link as LinkIcon, CheckCircle2, Circle } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

interface DeleteConfirm { id: number; url: string; }

async function adminFetch(path: string, init?: RequestInit, token?: string | null) {
  const res = await fetch(path, {
    ...init,
    headers: { ...(init?.headers ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error ?? res.statusText); }
  return res.json();
}

interface DirectUrl {
  id: number;
  schoolNo: string;
  schoolName: string | null;
  school: number | null;
  schoolDistrict: string | null;
  schoolType: string | null;
  tenderUrl: string;
  strategy: string;
  foundAt: string;
  lastUsedAt: string;
  useCount: number;
  historyTendersProcessed: boolean | null;
  analyzerCreated: boolean | null;
}

interface SchoolSuggestion {
  id: number;
  name: string;
  district: string;
  notes: string | null;
  schoolNo: string | null;
}

const STRATEGY_LABELS: Record<string, string> = {
  seed: "種子",
  scraped: "爬取",
  manual: "手動",
  "?": "未知",
};

function StrategyBadge({ strategy }: { strategy: string }) {
  const colors: Record<string, string> = {
    seed: "bg-blue-50 text-blue-700 border-blue-200",
    scraped: "bg-green-50 text-green-700 border-green-200",
    manual: "bg-amber-50 text-amber-700 border-amber-200",
    "?": "bg-gray-50 text-gray-500 border-gray-200",
  };
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium border ${colors[strategy] ?? colors["?"]}`}>
      {STRATEGY_LABELS[strategy] ?? strategy}
    </span>
  );
}

export default function AdminSchoolsDirecturlPage() {
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const af = async (path: string, init?: RequestInit) => adminFetch(path, init, await getToken());
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [editRow, setEditRow] = useState<DirectUrl | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirm | null>(null);

  const { data: rows = [], isLoading } = useQuery<DirectUrl[]>({
    queryKey: ["admin-schools-directurl"],
    queryFn: () => af("/api/admin/schools-directurl"),
  });

  const filtered = rows.filter(r =>
    !search ||
    r.schoolNo.includes(search) ||
    (r.schoolName ?? "").toLowerCase().includes(search.toLowerCase()) ||
    r.tenderUrl.toLowerCase().includes(search.toLowerCase())
  );

  // 按 schoolNo 分組，保持原本排序
  const groups: DirectUrl[][] = [];
  const groupMap = new Map<string, DirectUrl[]>();
  for (const r of filtered) {
    const g = groupMap.get(r.schoolNo);
    if (g) { g.push(r); }
    else { const ng = [r]; groupMap.set(r.schoolNo, ng); groups.push(ng); }
  }

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await af(`/api/admin/schools-directurl/${id}`, { method: "DELETE" });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-schools-directurl"] }); toast({ title: "已刪除" }); },
    onError: (e: any) => toast({ title: "錯誤", description: e.message, variant: "destructive" }),
  });

  const patchMutation = useMutation({
    mutationFn: async ({ id, field, value }: { id: number; field: string; value: boolean }) => {
      await af(`/api/admin/schools-directurl/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-schools-directurl"] }),
    onError: (e: any) => toast({ title: "錯誤", description: e.message, variant: "destructive" }),
  });

  return (
    <AdminLayout tab="schools-directurl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">招標頁管理</h2>
          <p className="text-sm text-muted-foreground mt-0.5">管理各學校招標專頁連結（共 {rows.length} 筆）</p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" /> 新增
        </Button>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="搜尋學校編號、名稱或連結…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-8 h-9 text-sm"
        />
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2.5 font-medium w-36">學校編號</th>
              <th className="text-left px-3 py-2.5 font-medium">學校名稱</th>
              <th className="text-left px-3 py-2.5 font-medium w-20 hidden md:table-cell">地區</th>
              <th className="text-left px-3 py-2.5 font-medium">招標頁連結</th>
              <th className="text-left px-3 py-2.5 font-medium w-16">類型</th>
              <th className="text-center px-3 py-2.5 font-medium w-20">歷史招標</th>
              <th className="text-center px-3 py-2.5 font-medium w-20">分析程式</th>
              <th className="text-right px-3 py-2.5 font-medium w-16">操作</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">載入中…</td></tr>
            )}
            {!isLoading && groups.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">未找到資料</td></tr>
            )}
            {groups.map((group, gi) => {
              const first = group[0];
              const isMulti = group.length > 1;
              const groupBg = gi % 2 === 0 ? "" : "bg-muted/10";
              return (
              <Fragment key={first.schoolNo}>
              {group.map((row, ri) => {
                const isFirst = ri === 0;
                return (
                  <tr
                    key={row.id}
                    className={`transition-colors hover:bg-yellow-50/60 ${groupBg} ${isFirst ? "border-t border-border" : isMulti ? "border-t border-dashed border-border/40" : ""}`}
                  >
                    {/* 學校編號 — 第一行才顯示，rowSpan 整組 */}
                    {isFirst && (
                      <td rowSpan={group.length} className="px-3 py-2 font-mono text-xs text-muted-foreground align-top">
                        {first.schoolNo}
                        {isMulti && (
                          <span className="ml-1 inline-flex items-center rounded px-1 py-0 text-[10px] bg-amber-50 text-amber-600 border border-amber-200 font-medium">
                            ×{group.length}
                          </span>
                        )}
                      </td>
                    )}
                    {/* 學校名稱 — 第一行才顯示，rowSpan 整組 */}
                    {isFirst && (
                      <td rowSpan={group.length} className="px-3 py-2 align-top">
                        <div className="font-medium leading-tight">{first.schoolName ?? <span className="text-muted-foreground/50 italic">未設定</span>}</div>
                        {first.school && <div className="flex items-center gap-0.5 text-xs text-emerald-600 mt-0.5"><LinkIcon className="h-2.5 w-2.5" /> 已連結</div>}
                      </td>
                    )}
                    {/* 地區 — 第一行才顯示，rowSpan 整組 */}
                    {isFirst && (
                      <td rowSpan={group.length} className="px-3 py-2 text-xs text-muted-foreground hidden md:table-cell align-top">{first.schoolDistrict ?? "—"}</td>
                    )}
                    {/* URL — 支援逗號分隔多條 URL，每條顯示「開啟」按鈕 */}
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1.5">
                        {row.tenderUrl.split(",").map((u, ui) => {
                          const url = u.trim();
                          if (!url) return null;
                          const urls = row.tenderUrl.split(",").filter(x => x.trim());
                          const label = urls.length > 1 ? `開啟 ${ui + 1}` : "開啟";
                          return (
                            <a
                              key={ui}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={url}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 hover:border-primary/40 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 shrink-0" />
                              {label}
                            </a>
                          );
                        })}
                      </div>
                    </td>
                    {/* 類型 */}
                    <td className="px-3 py-2"><StrategyBadge strategy={row.strategy} /></td>
                    {/* 歷史招標 */}
                    <td className="px-3 py-2 text-center">
                      <button
                        title={row.historyTendersProcessed ? "已處理（點擊改為未處理）" : "未處理（點擊改為已處理）"}
                        onClick={() => patchMutation.mutate({ id: row.id, field: "historyTendersProcessed", value: !row.historyTendersProcessed })}
                        disabled={patchMutation.isPending}
                        className="inline-flex flex-col items-center gap-0.5 group"
                      >
                        {row.historyTendersProcessed
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500 group-hover:text-emerald-600" />
                          : <Circle className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground" />
                        }
                        <span className={`text-[10px] leading-none ${row.historyTendersProcessed ? "text-emerald-600 font-medium" : "text-muted-foreground/60"}`}>
                          {row.historyTendersProcessed ? "已處理" : "未處理"}
                        </span>
                      </button>
                    </td>
                    {/* 分析程式 */}
                    <td className="px-3 py-2 text-center">
                      <button
                        title={row.analyzerCreated ? "已建立（點擊改為未建立）" : "未建立（點擊改為已建立）"}
                        onClick={() => patchMutation.mutate({ id: row.id, field: "analyzerCreated", value: !row.analyzerCreated })}
                        disabled={patchMutation.isPending}
                        className="inline-flex flex-col items-center gap-0.5 group"
                      >
                        {row.analyzerCreated
                          ? <CheckCircle2 className="h-4 w-4 text-blue-500 group-hover:text-blue-600" />
                          : <Circle className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground" />
                        }
                        <span className={`text-[10px] leading-none ${row.analyzerCreated ? "text-blue-600 font-medium" : "text-muted-foreground/60"}`}>
                          {row.analyzerCreated ? "已建立" : "未建立"}
                        </span>
                      </button>
                    </td>
                    {/* 操作 */}
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => setEditRow(row)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteConfirm({ id: row.id, url: row.tenderUrl })}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {editRow && (
        <EditDialog
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["admin-schools-directurl"] }); setEditRow(null); }}
        />
      )}
      {addOpen && (
        <AddDialog
          onClose={() => setAddOpen(false)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["admin-schools-directurl"] }); setAddOpen(false); }}
        />
      )}
      <ConfirmDialog
        open={deleteConfirm !== null}
        title="刪除記錄"
        description={deleteConfirm ? `確定刪除 #${deleteConfirm.id}？\n${deleteConfirm.url}` : ""}
        confirmLabel="確定刪除"
        destructive
        onConfirm={() => { const id = deleteConfirm!.id; setDeleteConfirm(null); deleteMutation.mutate(id); }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </AdminLayout>
  );
}

function SchoolSearch({ value, onChange }: {
  value: { id: number | null; name: string };
  onChange: (v: { id: number | null; name: string; district?: string; notes?: string | null; schoolNo?: string | null }) => void;
}) {
  const [q, setQ] = useState(value.name);
  const [open, setOpen] = useState(false);
  const { getToken } = useAuth();
  const dropRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [dq, setDq] = useState("");

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.length >= 1) { timer.current = setTimeout(() => setDq(q), 300); }
    else setDq("");
  }, [q]);

  const { data: suggestions = [] } = useQuery<SchoolSuggestion[]>({
    queryKey: ["school-search", dq],
    queryFn: async () => adminFetch(`/api/admin/schools/search?q=${encodeURIComponent(dq)}`, undefined, await getToken()),
    enabled: dq.length >= 1,
  });

  useEffect(() => {
    function close(e: MouseEvent) { if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={dropRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); onChange({ id: null, name: e.target.value }); }}
          onFocus={() => setOpen(true)}
          placeholder="輸入學校名稱搜尋…"
          className="pl-8 h-9 text-sm"
        />
        {value.id && <span className="absolute right-2.5 top-2 text-xs text-emerald-600 flex items-center gap-1"><LinkIcon className="h-3 w-3" />已連結</span>}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s.id}
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex justify-between items-center"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { setQ(s.name); onChange({ id: s.id, name: s.name, district: s.district, notes: s.notes, schoolNo: s.schoolNo }); setOpen(false); }}
            >
              <span>{s.name}</span>
              <span className="text-xs text-muted-foreground">{s.district}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ToggleField({ label, value, onChange, activeColor = "emerald" }: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  activeColor?: "emerald" | "blue";
}) {
  const active = activeColor === "emerald"
    ? "bg-emerald-50 border-emerald-400 text-emerald-700"
    : "bg-blue-50 border-blue-400 text-blue-700";
  const inactive = "bg-muted/30 border-border text-muted-foreground";
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`flex-1 py-2 text-sm font-medium rounded-md border transition-all ${value ? active : inactive}`}
      >
        ✓ 已{label === "歷史招標" ? "處理" : "建立"}
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`flex-1 py-2 text-sm font-medium rounded-md border transition-all ${!value ? "bg-muted/30 border-border text-muted-foreground ring-1 ring-inset ring-muted-foreground/30" : inactive}`}
      >
        ✗ 未{label === "歷史招標" ? "處理" : "建立"}
      </button>
    </div>
  );
}

function EditDialog({ row, onClose, onSaved }: { row: DirectUrl; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const { getToken } = useAuth();
  const [schoolNo, setSchoolNo] = useState(row.schoolNo);
  const [schoolField, setSchoolField] = useState<{ id: number | null; name: string }>({
    id: row.school,
    name: row.schoolName ?? "",
  });
  const [schoolNotes, setSchoolNotes] = useState<string | null>(null);
  const [tenderUrl, setTenderUrl] = useState(row.tenderUrl);
  const [strategy, setStrategy] = useState(row.strategy);
  const [historyTendersProcessed, setHistoryTendersProcessed] = useState<boolean>(row.historyTendersProcessed ?? false);
  const [analyzerCreated, setAnalyzerCreated] = useState<boolean>(row.analyzerCreated ?? false);

  const mutation = useMutation({
    mutationFn: async () => adminFetch(`/api/admin/schools-directurl/${row.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ schoolNo, schoolName: schoolField.name || null, school: schoolField.id, tenderUrl, strategy, historyTendersProcessed, analyzerCreated }),
    }, await getToken()),
    onSuccess: () => { toast({ title: "已更新" }); onSaved(); },
    onError: (e: any) => toast({ title: "錯誤", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>修改招標頁連結 <span className="text-sm font-normal text-muted-foreground">#{row.id}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs mb-1.5 block">學校編號</Label>
            <Input value={schoolNo} onChange={e => setSchoolNo(e.target.value)} className="font-mono text-sm" placeholder="學校編號" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">學校名稱 <span className="text-muted-foreground font-normal">（搜尋可自動連結 FK）</span></Label>
            <SchoolSearch
              value={schoolField}
              onChange={v => { setSchoolField({ id: v.id, name: v.name }); setSchoolNotes(v.notes ?? null); }}
            />
            {schoolField.id && (
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-emerald-600">已連結至 schools.id = {schoolField.id}</p>
                {schoolNotes && <p className="text-xs text-muted-foreground">schools.notes = {schoolNotes}</p>}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">招標頁 URL <span className="text-red-500">*</span></Label>
            <Input value={tenderUrl} onChange={e => setTenderUrl(e.target.value)} placeholder="https://…" />
            {tenderUrl && (
              <a href={tenderUrl} target="_blank" rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1 mt-1.5">
                <ExternalLink className="h-3 w-3" /> 在新頁開啟預覽
              </a>
            )}
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">類型</Label>
            <Select value={strategy} onValueChange={setStrategy}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="seed">種子（seed）</SelectItem>
                <SelectItem value="scraped">爬取（scraped）</SelectItem>
                <SelectItem value="manual">手動（manual）</SelectItem>
                <SelectItem value="?">未知（?）</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="h-px bg-border" />
          <div className="space-y-1.5">
            <Label className="text-xs mb-1.5 block">歷史招標</Label>
            <ToggleField label="歷史招標" value={historyTendersProcessed} onChange={setHistoryTendersProcessed} activeColor="emerald" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs mb-1.5 block">分析程式</Label>
            <ToggleField label="分析程式" value={analyzerCreated} onChange={setAnalyzerCreated} activeColor="blue" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={() => mutation.mutate()} disabled={!tenderUrl || mutation.isPending}>
            {mutation.isPending ? "儲存中…" : "儲存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const { getToken } = useAuth();
  const [schoolNo, setSchoolNo] = useState("");
  const [schoolNoAutoFilled, setSchoolNoAutoFilled] = useState(false);
  const [schoolField, setSchoolField] = useState<{ id: number | null; name: string }>({ id: null, name: "" });
  const [schoolNotes, setSchoolNotes] = useState<string | null>(null);
  const [tenderUrl, setTenderUrl] = useState("");
  const [strategy, setStrategy] = useState("manual");

  const mutation = useMutation({
    mutationFn: async () => adminFetch("/api/admin/schools-directurl", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ schoolNo, schoolName: schoolField.name || null, school: schoolField.id, tenderUrl, strategy }),
    }, await getToken()),
    onSuccess: () => { toast({ title: "已新增" }); onSaved(); },
    onError: (e: any) => toast({ title: "錯誤", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>新增學校招標頁</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs mb-1.5 block">學校名稱 <span className="text-muted-foreground font-normal">（搜尋可自動填入學校編號）</span></Label>
            <SchoolSearch
              value={schoolField}
              onChange={v => {
                setSchoolField({ id: v.id, name: v.name });
                setSchoolNotes(v.notes ?? null);
                if (v.id) {
                  // 優先用 schools_directurl.school_no，其次從 notes 提取
                  const fromNotes = v.notes?.match(/學校編號[：:]\s*(\S+)/)?.[1] ?? null;
                  const resolved = v.schoolNo || fromNotes;
                  if (resolved) {
                    setSchoolNo(resolved);
                    setSchoolNoAutoFilled(true);
                  } else {
                    setSchoolNo("");
                    setSchoolNoAutoFilled(false);
                  }
                } else {
                  if (schoolNoAutoFilled) { setSchoolNo(""); setSchoolNoAutoFilled(false); }
                }
              }}
            />
            {schoolField.id && (
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-emerald-600">已連結至 schools.id = {schoolField.id}</p>
                {schoolNotes && <p className="text-xs text-muted-foreground">schools.notes = {schoolNotes}</p>}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">學校編號 <span className="text-muted-foreground font-normal">（選填，留空自動生成）</span></Label>
            <div className="relative">
              <Input
                value={schoolNo}
                onChange={e => { setSchoolNo(e.target.value); setSchoolNoAutoFilled(false); }}
                placeholder="留空則自動生成，或輸入 12 位編號"
                className={`font-mono text-sm ${schoolNoAutoFilled ? "pr-16 border-emerald-400 focus-visible:ring-emerald-400" : ""}`}
              />
              {schoolNoAutoFilled && (
                <span className="absolute right-2.5 top-2 text-xs text-emerald-600 flex items-center gap-0.5">
                  <LinkIcon className="h-3 w-3" />自動填入
                </span>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">招標頁 URL <span className="text-red-500">*</span></Label>
            <Input value={tenderUrl} onChange={e => setTenderUrl(e.target.value)} placeholder="https://…" />
            {tenderUrl && (
              <a href={tenderUrl} target="_blank" rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1 mt-1.5">
                <ExternalLink className="h-3 w-3" /> 在新頁開啟預覽
              </a>
            )}
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">類型</Label>
            <Select value={strategy} onValueChange={setStrategy}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="seed">種子（seed）</SelectItem>
                <SelectItem value="scraped">爬取（scraped）</SelectItem>
                <SelectItem value="manual">手動（manual）</SelectItem>
                <SelectItem value="?">未知（?）</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={() => mutation.mutate()} disabled={!tenderUrl || mutation.isPending}>
            {mutation.isPending ? "新增中…" : "新增"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
